// src/pages/ProfilePage.tsx
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { authApi } from "../lib/authApi";
import { gamificationApi } from "../lib/gamificationApi";
import { showSuccessToast, showErrorToast } from "../lib/toast";
import {
  Mail,
  Calendar,
  BookOpen,
  Camera,
  Edit,
  Clock,
  Award,
  ChevronRight,
  X,
  Loader2,
  User,
  Shield,
  AlertTriangle,
  MapPin,
  Phone,
  Globe,
  Linkedin,
  Github,
  Twitter,
  Sparkles,
  Trophy,
  Settings,
  Users,
  Star,
  CheckCircle,
  Crown,
  GraduationCap,
  Briefcase,
  Zap,
  TrendingUp,
  Heart,
  MessageCircle,
  Share2,
  Facebook,
  UserCircle,
} from "lucide-react";

interface RoleConfig {
  label: string;
  icon: any;
  gradient: string;
  softBg: string;
  textColor: string;
  borderColor: string;
  description: string;
}

const roleConfigs: Record<string, RoleConfig> = {
  student: {
    label: "Student",
    icon: GraduationCap,
    gradient: "from-violet-500 to-purple-600",
    softBg: "bg-violet-50",
    textColor: "text-violet-700",
    borderColor: "border-violet-200",
    description: "Learning and growing through courses",
  },
  instructor: {
    label: "Instructor",
    icon: Briefcase,
    gradient: "from-sky-500 to-blue-600",
    softBg: "bg-sky-50",
    textColor: "text-sky-700",
    borderColor: "border-sky-200",
    description: "Creating and teaching courses",
  },
  mentor: {
    label: "Mentor",
    icon: Users,
    gradient: "from-emerald-500 to-teal-600",
    softBg: "bg-emerald-50",
    textColor: "text-emerald-700",
    borderColor: "border-emerald-200",
    description: "Guiding and supporting students",
  },
  admin: {
    label: "Admin",
    icon: Crown,
    gradient: "from-orange-500 to-red-500",
    softBg: "bg-orange-50",
    textColor: "text-orange-700",
    borderColor: "border-orange-200",
    description: "Platform management and oversight",
  },
};

const ProfilePage = () => {
  const { user, updateProfile, uploadAvatar, isLoading } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<"overview" | "settings">(
    "overview",
  );
  const [gamificationStats, setGamificationStats] = useState<{
    coursesCompleted: number;
    totalHoursLearned: number;
    currentStreak: number;
    totalPoints: number;
    level: number;
  } | null>(null);
  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    bio: "",
    phoneNumber: "",
    location: "",
    socialLinks: {
      website: "",
      linkedin: "",
      github: "",
      twitter: "",
      facebook: "",
    },
    role: "",
    avatar: "",
    coursesCount: 0,
    completedCourses: 0,
    totalHours: 0,
    certificates: 0,
    reputation: 0,
    streak: 0,
    followers: 0,
    following: 0,
    isActive: true,
    isBlocked: false,
    isSuspended: false,
    lastLogin: "",
    createdAt: "",
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    if (!isLoading && !user) navigate("/login");
  }, [user, navigate, isLoading]);

  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        username: user.username || "",
        bio: user.bio || "No bio added yet",
        phoneNumber: user.phoneNumber || "",
        location: user.location || "Not specified",
        socialLinks: {
          website: user.socialLinks?.website || "",
          linkedin: user.socialLinks?.linkedin || "",
          github: user.socialLinks?.github || "",
          twitter: user.socialLinks?.twitter || "",
          facebook: user.socialLinks?.facebook || "",
        },
        role: user.role || "student",
        avatar: user.avatar || "",
        coursesCount:
          typeof user.coursesCount === "number" ? user.coursesCount : 0,
        completedCourses:
          typeof user.completedCourses === "number" ? user.completedCourses : 0,
        totalHours: typeof user.totalHours === "number" ? user.totalHours : 0,
        certificates:
          typeof user.certificates === "number" ? user.certificates : 0,
        reputation: typeof user.reputation === "number" ? user.reputation : 0,
        streak: typeof user.streak === "number" ? user.streak : 0,
        followers: typeof user.followers === "number" ? user.followers : 0,
        following: typeof user.following === "number" ? user.following : 0,
        isActive: user.isActive ?? true,
        isBlocked: user.isBlocked || false,
        isSuspended: user.isSuspended || false,
        lastLogin: user.lastLogin
          ? new Date(user.lastLogin).toLocaleString()
          : new Date().toLocaleString(),
        createdAt: user.createdAt
          ? new Date(user.createdAt).toLocaleDateString("en-US", {
              month: "long",
              year: "numeric",
            })
          : "January 2024",
      });
    }
  }, [user]);

  useEffect(() => {
    const fetchGamificationStats = async () => {
      try {
        const res = await gamificationApi.getMyStats();
        if (res.success && res.data) {
          setGamificationStats({
            coursesCompleted:
              typeof res.data.coursesCompleted === "number"
                ? res.data.coursesCompleted
                : 0,
            totalHoursLearned:
              typeof res.data.totalHoursLearned === "number"
                ? res.data.totalHoursLearned
                : 0,
            currentStreak:
              typeof res.data.currentStreak === "number"
                ? res.data.currentStreak
                : 0,
            totalPoints:
              typeof res.data.totalPoints === "number"
                ? res.data.totalPoints
                : 0,
            level: typeof res.data.level === "number" ? res.data.level : 1,
          });
        }
      } catch (err) {
        console.error("Failed to fetch gamification stats:", err);
      }
    };
    if (!isLoading && user) fetchGamificationStats();
  }, [user, isLoading]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-orange-500 animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading profile...</p>
        </div>
      </div>
    );
  }

  const roleConfig = roleConfigs[profileData.role] || roleConfigs.student;
  const RoleIcon = roleConfig.icon;
  const displayName =
    profileData.firstName && profileData.lastName
      ? `${profileData.firstName} ${profileData.lastName}`
      : profileData.username;

  const avatarSrc =
    profileData.avatar ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=f97316&color=fff&size=256&bold=true&length=2`;

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      await updateProfile({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        bio: profileData.bio || undefined,
        phoneNumber: profileData.phoneNumber || undefined,
        location: profileData.location || undefined,
        socialLinks: {
          website: profileData.socialLinks.website || undefined,
          linkedin: profileData.socialLinks.linkedin || undefined,
          github: profileData.socialLinks.github || undefined,
          twitter: profileData.socialLinks.twitter || undefined,
          facebook: profileData.socialLinks.facebook || undefined,
        },
      });
      showSuccessToast("Profile updated successfully!");
      setIsEditing(false);
    } catch (error: unknown) {
      showErrorToast((error as Error).message || "Failed to update profile");
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showErrorToast("Passwords do not match");
      return;
    }
    if (passwordData.newPassword.length < 6) {
      showErrorToast("Password must be at least 6 characters");
      return;
    }
    setIsChangingPassword(true);
    try {
      await authApi.changePassword(passwordData);
      showSuccessToast("Password changed successfully!");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setShowPasswordModal(false);
    } catch (error: unknown) {
      showErrorToast((error as Error).message || "Failed to change password");
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (
      !window.confirm(
        "Are you sure you want to delete your account? This action cannot be undone.",
      )
    )
      return;
    setIsDeleting(true);
    try {
      await authApi.deleteAccount();
      showSuccessToast("Account deleted successfully");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("user");
      window.location.href = "/";
    } catch (error: unknown) {
      showErrorToast((error as Error).message || "Failed to delete account");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showErrorToast("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showErrorToast("Image must be less than 5MB");
      return;
    }
    setIsUploadingAvatar(true);
    try {
      const avatarUrl = await uploadAvatar(file);
      setProfileData((prev) => ({ ...prev, avatar: avatarUrl }));
      showSuccessToast("Avatar updated!");
    } catch (error: unknown) {
      showErrorToast((error as Error).message || "Failed to upload avatar");
    } finally {
      setIsUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };


  const inputCls =
    "w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent focus:bg-white transition";

  const getSafeNumber = (value: any, defaultValue: number = 0): number => {
    if (typeof value === "number") return value;
    if (typeof value === "string" && !isNaN(Number(value)))
      return Number(value);
    return defaultValue;
  };

  const stats = [
    {
      label: "Courses",
      value: getSafeNumber(profileData.coursesCount),
      icon: BookOpen,
      color: "text-blue-500",
      bg: "bg-blue-50",
    },
    {
      label: "Completed",
      value:
        getSafeNumber(gamificationStats?.coursesCompleted) ||
        getSafeNumber(profileData.completedCourses),
      icon: CheckCircle,
      color: "text-emerald-500",
      bg: "bg-emerald-50",
    },
    {
      label: "Hours",
      value:
        getSafeNumber(gamificationStats?.totalHoursLearned) ||
        getSafeNumber(profileData.totalHours),
      icon: Clock,
      color: "text-orange-500",
      bg: "bg-orange-50",
    },
    {
      label: "Certificates",
      value: getSafeNumber(profileData.certificates),
      icon: Award,
      color: "text-purple-500",
      bg: "bg-purple-50",
    },
    {
      label: "Points",
      value:
        getSafeNumber(gamificationStats?.totalPoints) ||
        getSafeNumber(profileData.reputation),
      icon: Star,
      color: "text-yellow-500",
      bg: "bg-yellow-50",
    },
    {
      label: "Streak",
      value:
        getSafeNumber(gamificationStats?.currentStreak) ||
        getSafeNumber(profileData.streak),
      icon: Zap,
      color: "text-red-500",
      bg: "bg-red-50",
    },
  ];

  return (
    <div className="min-h-screen py-8 my-10 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-8">
      <div className="px-4 mx-auto max-w-6xl md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 mt-3">
          <div className="flex items-center gap-3 mb-1">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-r from-amber-600 to-orange-500 shadow-md">
              <UserCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 md:text-2xl">
                My Profile
              </h1>
              <p className="text-sm text-gray-500">
                View and manage your personal information
              </p>
            </div>
          </div>
        </div>

        {/* Profile Card */}
        <div className="mb-6 overflow-hidden bg-white border border-gray-100 shadow-md rounded-2xl">
          {/* Gradient Banner */}
          <div
            className={`h-24 md:h-32 bg-gradient-to-r ${roleConfig.gradient} relative`}
          >
            <div
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 30% 60%, rgba(255,255,255,0.4) 1px, transparent 1px), radial-gradient(circle at 70% 30%, rgba(255,255,255,0.3) 1px, transparent 1px)",
                backgroundSize: "28px 28px, 40px 40px",
              }}
            />
          </div>

          <div className="px-6 pb-6 -mt-12 sm:px-8">
            {/* Avatar Section */}
            <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
              <div className="relative">
                <div className="w-24 h-24 overflow-hidden shadow-lg bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl ring-4 ring-white">
                  <img
                    src={avatarSrc}
                    alt={displayName}
                    className="object-cover w-full h-full"
                  />
                  {isUploadingAvatar && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <Loader2 className="w-6 h-6 text-white animate-spin" />
                    </div>
                  )}
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingAvatar}
                  className="absolute flex items-center justify-center w-7 h-7 transition bg-white border border-gray-200 rounded-lg shadow-md -bottom-1 -right-1 hover:bg-gray-50 disabled:opacity-50"
                >
                  <Camera className="w-3.5 h-3.5 text-gray-600" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition shadow-sm"
                >
                  <Edit className="w-4 h-4" />
                  Edit Profile
                </button>
              </div>
            </div>

            {/* User Info */}
            <div className="mb-4">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">
                  {displayName}
                </h2>
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${roleConfig.softBg} ${roleConfig.textColor} ${roleConfig.borderColor} border`}
                >
                  <RoleIcon className="w-3.5 h-3.5" />
                  {roleConfig.label}
                </span>
              </div>
              <p className="text-sm text-gray-400 mb-2">
                @{profileData.username}
              </p>
              <div className="flex flex-wrap gap-3 mb-3 text-sm text-gray-500">
                {profileData.location &&
                  profileData.location !== "Not specified" && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {profileData.location}
                    </span>
                  )}
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Joined {profileData.createdAt}
                </span>
              </div>
              <p className="text-base text-gray-600 leading-relaxed max-w-2xl">
                {profileData.bio}
              </p>
            </div>

            {/* Social Links */}
            {(profileData.socialLinks.website ||
              profileData.socialLinks.github ||
              profileData.socialLinks.twitter ||
              profileData.socialLinks.linkedin) && (
              <div className="flex flex-wrap gap-2 pb-4 mb-4 border-b border-gray-100">
                {profileData.socialLinks.website && (
                  <a
                    href={profileData.socialLinks.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg text-sm text-gray-600 hover:bg-gray-200 transition"
                  >
                    <Globe className="w-4 h-4" /> Website
                  </a>
                )}
                {profileData.socialLinks.github && (
                  <a
                    href={`https://github.com/${profileData.socialLinks.github}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 rounded-lg text-sm text-white hover:bg-gray-900 transition"
                  >
                    <Github className="w-4 h-4" /> GitHub
                  </a>
                )}
                {profileData.socialLinks.twitter && (
                  <a
                    href={`https://twitter.com/${profileData.socialLinks.twitter}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-1.5 bg-sky-100 rounded-lg text-sm text-sky-700 hover:bg-sky-200 transition"
                  >
                    <Twitter className="w-4 h-4" /> Twitter
                  </a>
                )}
                {profileData.socialLinks.linkedin && (
                  <a
                    href={`https://linkedin.com/in/${profileData.socialLinks.linkedin}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-1.5 bg-blue-100 rounded-lg text-sm text-blue-700 hover:bg-blue-200 transition"
                  >
                    <Linkedin className="w-4 h-4" /> LinkedIn
                  </a>
                )}
              </div>
            )}

            {/* Stats Grid - 6 columns on desktop */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {stats.map((stat, i) => (
                <div
                  key={i}
                  className={`${stat.bg} rounded-xl p-3 text-center`}
                >
                  <stat.icon className={`w-5 h-5 mx-auto mb-1 ${stat.color}`} />
                  <p className="text-xl font-bold text-gray-900">
                    {stat.value}
                  </p>
                  <p className="text-xs text-gray-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 p-1 mb-6 bg-white border border-gray-100 shadow-sm rounded-xl w-fit">
          {[
            { id: "overview", label: "Overview", icon: User },
            { id: "settings", label: "Settings", icon: Settings },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg transition-all ${
                activeTab === tab.id
                  ? "bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-md"
                  : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="grid gap-6 md:grid-cols-3">
            {/* Left Column - 2 cols on desktop */}
            <div className="space-y-6 md:col-span-2">
              {/* Personal Info */}
              <div className="p-6 bg-white border border-gray-100 shadow-sm rounded-2xl">
                <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 mb-4">
                  <User className="w-5 h-5 text-orange-500" />
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {[
                    {
                      label: "First Name",
                      value: profileData.firstName || "—",
                    },
                    { label: "Last Name", value: profileData.lastName || "—" },
                    { label: "Username", value: `@${profileData.username}` },
                    { label: "Email", value: profileData.email },
                    { label: "Phone", value: profileData.phoneNumber || "—" },
                    { label: "Location", value: profileData.location || "—" },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="flex justify-between py-2 border-b border-gray-100"
                    >
                      <span className="text-sm text-gray-500">
                        {item.label}
                      </span>
                      <span className="text-sm font-medium text-gray-800">
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bio */}
              <div className="p-6 bg-white border border-gray-100 shadow-sm rounded-2xl">
                <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 mb-3">
                  <MessageCircle className="w-5 h-5 text-orange-500" />
                  Bio
                </h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  {profileData.bio}
                </p>
              </div>

              {/* Social Links Detailed */}
              {(profileData.socialLinks.website ||
                profileData.socialLinks.github ||
                profileData.socialLinks.twitter ||
                profileData.socialLinks.linkedin) && (
                <div className="p-6 bg-white border border-gray-100 shadow-sm rounded-2xl">
                  <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 mb-4">
                    <Share2 className="w-5 h-5 text-orange-500" />
                    Social Links
                  </h3>
                  <div className="space-y-3">
                    {profileData.socialLinks.website && (
                      <a
                        href={profileData.socialLinks.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition"
                      >
                        <Globe className="w-5 h-5 text-gray-500" />
                        <span className="text-sm text-gray-700 flex-1 truncate">
                          {profileData.socialLinks.website}
                        </span>
                      </a>
                    )}
                    {profileData.socialLinks.github && (
                      <a
                        href={`https://github.com/${profileData.socialLinks.github}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition"
                      >
                        <Github className="w-5 h-5 text-gray-700" />
                        <span className="text-sm text-gray-700 flex-1">
                          @{profileData.socialLinks.github}
                        </span>
                      </a>
                    )}
                    {profileData.socialLinks.twitter && (
                      <a
                        href={`https://twitter.com/${profileData.socialLinks.twitter}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition"
                      >
                        <Twitter className="w-5 h-5 text-sky-500" />
                        <span className="text-sm text-gray-700 flex-1">
                          @{profileData.socialLinks.twitter}
                        </span>
                      </a>
                    )}
                    {profileData.socialLinks.linkedin && (
                      <a
                        href={`https://linkedin.com/in/${profileData.socialLinks.linkedin}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition"
                      >
                        <Linkedin className="w-5 h-5 text-blue-600" />
                        <span className="text-sm text-gray-700 flex-1">
                          @{profileData.socialLinks.linkedin}
                        </span>
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Role Card */}
              <div
                className={`rounded-2xl p-6 border ${roleConfig.borderColor} ${roleConfig.softBg}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className={`w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm ${roleConfig.textColor}`}
                  >
                    <RoleIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Current Role</p>
                    <p className={`text-xl font-bold ${roleConfig.textColor}`}>
                      {roleConfig.label}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  {roleConfig.description}
                </p>
                <div className="flex items-center justify-between pt-3 text-sm border-t border-gray-200">
                  <span className="text-gray-500">Account Status</span>
                  <span
                    className={`font-semibold ${profileData.isActive ? "text-emerald-600" : "text-red-600"}`}
                  >
                    {profileData.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>

              {/* Community Stats */}
              <div className="p-6 bg-white border border-gray-100 shadow-sm rounded-2xl">
                <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 mb-4">
                  <Users className="w-5 h-5 text-orange-500" />
                  Community
                </h3>
                <div className="space-y-3">
                  {[
                    {
                      label: "Followers",
                      value: getSafeNumber(profileData.followers),
                      icon: Heart,
                      color: "text-red-400",
                    },
                    {
                      label: "Following",
                      value: getSafeNumber(profileData.following),
                      icon: Users,
                      color: "text-blue-400",
                    },
                    {
                      label: "Level",
                      value: getSafeNumber(gamificationStats?.level, 1),
                      icon: Trophy,
                      color: "text-yellow-500",
                    },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-xl"
                    >
                      <div className="flex items-center gap-2">
                        <item.icon className={`w-5 h-5 ${item.color}`} />
                        <span className="text-sm text-gray-600">
                          {item.label}
                        </span>
                      </div>
                      <span className="text-lg font-bold text-gray-900">
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <div className="grid gap-6 md:grid-cols-2">
            {/* Account Settings */}
            <div className="p-6 bg-white border border-gray-100 shadow-sm rounded-2xl">
              <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 mb-5">
                <Settings className="w-5 h-5 text-orange-500" />
                Account Settings
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={profileData.firstName}
                      onChange={(e) =>
                        setProfileData({
                          ...profileData,
                          firstName: e.target.value,
                        })
                      }
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={profileData.lastName}
                      onChange={(e) =>
                        setProfileData({
                          ...profileData,
                          lastName: e.target.value,
                        })
                      }
                      className={inputCls}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bio
                  </label>
                  <textarea
                    rows={3}
                    value={profileData.bio}
                    onChange={(e) =>
                      setProfileData({ ...profileData, bio: e.target.value })
                    }
                    className={inputCls}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={profileData.phoneNumber}
                      onChange={(e) =>
                        setProfileData({
                          ...profileData,
                          phoneNumber: e.target.value,
                        })
                      }
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Location
                    </label>
                    <input
                      type="text"
                      value={profileData.location}
                      onChange={(e) =>
                        setProfileData({
                          ...profileData,
                          location: e.target.value,
                        })
                      }
                      className={inputCls}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Website
                  </label>
                  <input
                    type="url"
                    value={profileData.socialLinks.website}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        socialLinks: {
                          ...profileData.socialLinks,
                          website: e.target.value,
                        },
                      })
                    }
                    className={inputCls}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      LinkedIn
                    </label>
                    <input
                      type="text"
                      placeholder="username"
                      value={profileData.socialLinks.linkedin}
                      onChange={(e) =>
                        setProfileData({
                          ...profileData,
                          socialLinks: {
                            ...profileData.socialLinks,
                            linkedin: e.target.value,
                          },
                        })
                      }
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      GitHub
                    </label>
                    <input
                      type="text"
                      placeholder="username"
                      value={profileData.socialLinks.github}
                      onChange={(e) =>
                        setProfileData({
                          ...profileData,
                          socialLinks: {
                            ...profileData.socialLinks,
                            github: e.target.value,
                          },
                        })
                      }
                      className={inputCls}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Twitter
                  </label>
                  <input
                    type="text"
                    placeholder="@username"
                    value={profileData.socialLinks.twitter}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        socialLinks: {
                          ...profileData.socialLinks,
                          twitter: e.target.value,
                        },
                      })
                    }
                    className={inputCls}
                  />
                </div>
                <button
                  onClick={handleSaveProfile}
                  disabled={isSaving}
                  className="w-full py-2.5 text-sm font-medium text-white bg-gradient-to-r from-orange-600 to-orange-500 rounded-xl hover:shadow-md transition-all disabled:opacity-50"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </div>

            {/* Security & Danger */}
            <div className="space-y-6">
              <div className="p-6 bg-white border border-gray-100 shadow-sm rounded-2xl">
                <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 mb-4">
                  <Shield className="w-5 h-5 text-orange-500" />
                  Security
                </h3>
                <button
                  onClick={() => setShowPasswordModal(true)}
                  className="flex items-center justify-between w-full px-4 py-3 text-sm border border-gray-200 rounded-xl hover:bg-gray-50 transition"
                >
                  <span className="text-gray-700">Change Password</span>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <div className="p-6 bg-white border border-red-100 shadow-sm rounded-2xl">
                <h3 className="flex items-center gap-2 text-base font-semibold text-red-600 mb-2">
                  <AlertTriangle className="w-5 h-5" />
                  Danger Zone
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Once deleted, your account cannot be recovered.
                </p>
                <button
                  onClick={handleDeleteAccount}
                  disabled={isDeleting}
                  className="w-full py-2.5 text-sm font-medium text-red-600 border border-red-200 rounded-xl hover:bg-red-50 transition"
                >
                  {isDeleting ? (
                    <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                  ) : (
                    "Delete Account"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {isEditing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 flex items-center justify-between p-5 bg-white border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">Edit Profile</h2>
              <button
                onClick={() => setIsEditing(false)}
                className="p-2 rounded-xl hover:bg-gray-100"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={profileData.firstName}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        firstName: e.target.value,
                      })
                    }
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={profileData.lastName}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        lastName: e.target.value,
                      })
                    }
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bio
                </label>
                <textarea
                  rows={3}
                  value={profileData.bio}
                  onChange={(e) =>
                    setProfileData({ ...profileData, bio: e.target.value })
                  }
                  className={inputCls}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={profileData.phoneNumber}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        phoneNumber: e.target.value,
                      })
                    }
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    value={profileData.location}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        location: e.target.value,
                      })
                    }
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Website
                </label>
                <input
                  type="url"
                  value={profileData.socialLinks.website}
                  onChange={(e) =>
                    setProfileData({
                      ...profileData,
                      socialLinks: {
                        ...profileData.socialLinks,
                        website: e.target.value,
                      },
                    })
                  }
                  className={inputCls}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    LinkedIn
                  </label>
                  <input
                    type="text"
                    placeholder="username"
                    value={profileData.socialLinks.linkedin}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        socialLinks: {
                          ...profileData.socialLinks,
                          linkedin: e.target.value,
                        },
                      })
                    }
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    GitHub
                  </label>
                  <input
                    type="text"
                    placeholder="username"
                    value={profileData.socialLinks.github}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        socialLinks: {
                          ...profileData.socialLinks,
                          github: e.target.value,
                        },
                      })
                    }
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Twitter
                </label>
                <input
                  type="text"
                  placeholder="@username"
                  value={profileData.socialLinks.twitter}
                  onChange={(e) =>
                    setProfileData({
                      ...profileData,
                      socialLinks: {
                        ...profileData.socialLinks,
                        twitter: e.target.value,
                      },
                    })
                  }
                  className={inputCls}
                />
              </div>
            </div>
            <div className="sticky bottom-0 flex gap-3 p-5 bg-white border-t border-gray-100">
              <button
                onClick={handleSaveProfile}
                disabled={isSaving}
                className="flex-1 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-orange-600 to-orange-500 rounded-xl hover:shadow-md transition-all disabled:opacity-50"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                ) : (
                  "Save Changes"
                )}
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="flex-1 py-2.5 text-sm font-medium border border-gray-200 rounded-xl hover:bg-gray-50 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-2xl">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">
                Change Password
              </h2>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({
                    currentPassword: "",
                    newPassword: "",
                    confirmPassword: "",
                  });
                }}
                className="p-2 rounded-xl hover:bg-gray-100"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      currentPassword: e.target.value,
                    })
                  }
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      newPassword: e.target.value,
                    })
                  }
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...profileData,
                      confirmPassword: e.target.value,
                    })
                  }
                  className={inputCls}
                />
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-gray-100">
              <button
                onClick={handleChangePassword}
                disabled={
                  isChangingPassword ||
                  !passwordData.currentPassword ||
                  !passwordData.newPassword ||
                  !passwordData.confirmPassword
                }
                className="flex-1 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-orange-600 to-orange-500 rounded-xl hover:shadow-md transition-all disabled:opacity-50"
              >
                {isChangingPassword ? (
                  <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                ) : (
                  "Change Password"
                )}
              </button>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({
                    currentPassword: "",
                    newPassword: "",
                    confirmPassword: "",
                  });
                }}
                className="flex-1 py-2.5 text-sm font-medium border border-gray-200 rounded-xl hover:bg-gray-50 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
